export interface City {
  slug: string;
  name: string;
  zips: string[];
  county: string;
  neighborhoods: string[];
  landmarks: string[];
  driveTimeFromHQ: string;
  blurb: string;
  homeStyles: string;
  localNote: string;
}

export interface CityService {
  slug: string;
  name: string;
  parentPath: string;
  shortDescription: string;
  heroImage: string;
  bullets: string[];
  cityAngle: (city: City) => string;
}

export const CITIES: City[] = [
  {
    slug: "newport-beach",
    name: "Newport Beach",
    zips: ["92660", "92661", "92662", "92663", "92657", "92625"],
    county: "Orange County",
    neighborhoods: [
      "Balboa Island",
      "Corona del Mar",
      "Newport Coast",
      "Lido Isle",
      "Newport Heights",
      "Big Canyon",
    ],
    landmarks: [
      "Balboa Pier",
      "Fashion Island",
      "Back Bay",
      "Newport Harbor",
    ],
    driveTimeFromHQ: "about 15 minutes",
    blurb:
      "Newport Beach is a high-end coastal community known for its waterfront estates, yacht harbor, and luxury shopping at Fashion Island.",
    homeStyles:
      "Mediterranean villas, modern coastal contemporaries, classic Cape Cod beach homes, and waterfront estates",
    localNote:
      "Salt air, marine humidity, and direct sun exposure make premium sealing and durable finishes essential for Newport Beach properties.",
  },
  {
    slug: "laguna-beach",
    name: "Laguna Beach",
    zips: ["92651", "92652", "92653", "92654", "92677"],
    county: "Orange County",
    neighborhoods: [
      "North Laguna",
      "Top of the World",
      "Three Arch Bay",
      "Emerald Bay",
      "South Laguna",
      "Bluebird Canyon",
    ],
    landmarks: [
      "Main Beach",
      "Heisler Park",
      "Pageant of the Masters",
      "Crescent Bay",
    ],
    driveTimeFromHQ: "about 20 minutes",
    blurb:
      "Laguna Beach is an artistic seaside village famous for its coves, hillside homes, and world-class galleries.",
    homeStyles:
      "Hillside contemporary architecture, Spanish revival, mid-century modern, and custom artist-designed homes",
    localNote:
      "Hillside foundations, ocean fog, and unique custom architecture demand finish work that respects the design intent of every Laguna home.",
  },
  {
    slug: "corona-del-mar",
    name: "Corona Del Mar",
    zips: ["92625", "92660"],
    county: "Orange County",
    neighborhoods: [
      "CDM Village",
      "Shore Cliffs",
      "Cameo Shores",
      "Irvine Terrace",
      "Spyglass Hill",
    ],
    landmarks: [
      "Big Corona Beach",
      "Inspiration Point",
      "Lookout Point",
      "Sherman Library & Gardens",
    ],
    driveTimeFromHQ: "about 15 minutes",
    blurb:
      "Corona del Mar is an upscale enclave inside Newport Beach, known for cliff-top ocean views and a charming village feel.",
    homeStyles:
      "Cape Cod cottages, Mediterranean estates, modern beach homes, and remodeled village bungalows",
    localNote:
      "Tight village lots and view-driven remodels mean clean staging, dust control, and meticulous finish work matter on every CDM project.",
  },
  {
    slug: "huntington-beach",
    name: "Huntington Beach",
    zips: ["92646", "92647", "92648", "92649"],
    county: "Orange County",
    neighborhoods: [
      "Downtown HB",
      "Huntington Harbour",
      "Sunset Beach",
      "Edwards Hill",
      "Seacliff",
    ],
    landmarks: [
      "Huntington Beach Pier",
      "Bolsa Chica Wetlands",
      "Pacific City",
      "Main Street",
    ],
    driveTimeFromHQ: "about 25 minutes",
    blurb:
      "Huntington Beach — Surf City USA — combines a relaxed beach-town lifestyle with one of the largest residential markets in coastal Orange County.",
    homeStyles:
      "Mid-century tract homes, Huntington Harbour waterfront properties, modern new builds, and 1970s remodels",
    localNote:
      "High humidity, sand, and family-heavy households mean HB homeowners need surfaces that are both beautiful and built for daily wear.",
  },
  {
    slug: "costa-mesa",
    name: "Costa Mesa",
    zips: ["92626", "92627", "92628"],
    county: "Orange County",
    neighborhoods: [
      "Mesa Verde",
      "Eastside Costa Mesa",
      "South Coast Metro",
      "Westside",
      "Halecrest",
    ],
    landmarks: [
      "South Coast Plaza",
      "OC Fair & Event Center",
      "The LAB",
      "Segerstrom Center for the Arts",
    ],
    driveTimeFromHQ: "about 10 minutes",
    blurb:
      "Costa Mesa is the design-and-arts heart of Orange County, home to South Coast Plaza and a thriving mix of mid-century neighborhoods.",
    homeStyles:
      "Mid-century ranch homes, Eichler-influenced moderns, updated 1960s tracts, and contemporary new construction",
    localNote:
      "Costa Mesa homeowners often blend mid-century character with modern upgrades — a balance our craftsmen specialize in protecting.",
  },
];

export const CITY_SERVICES: CityService[] = [
  {
    slug: "kitchen-remodeling",
    name: "Kitchen Remodeling",
    parentPath: "/kitchen-remodeling",
    shortDescription:
      "Full-service kitchen remodels — custom cabinetry, premium countertops, designer tile, and lighting from one trusted Orange County team.",
    heroImage:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=2000",
    bullets: [
      "Custom & semi-custom cabinetry",
      "Granite, quartz, and marble countertops",
      "Designer backsplash and tile installation",
      "Lighting and electrical upgrades",
      "Permits, inspections, and project management handled",
    ],
    cityAngle: (city) =>
      `${city.name} kitchens often anchor open-concept living, so we focus on layouts, lighting, and durable surfaces that match the way ${city.name} families actually live.`,
  },
  {
    slug: "bathroom-remodeling",
    name: "Bathroom Remodeling",
    parentPath: "/bathroom-remodeling",
    shortDescription:
      "Spa-quality bathroom renovations — walk-in showers, luxury vanities, waterproof tile, and modern fixtures expertly installed.",
    heroImage:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=2000",
    bullets: [
      "Custom walk-in and frameless glass showers",
      "Natural stone and porcelain tile",
      "Vanities, countertops, and modern fixtures",
      "Waterproofing and proper drainage detailing",
      "Aging-in-place and accessibility upgrades",
    ],
    cityAngle: (city) =>
      `Bathrooms in ${city.name} face constant moisture and salt-air exposure — our waterproofing and stone-sealing protocols are built for coastal Orange County conditions.`,
  },
  {
    slug: "hardwood-flooring",
    name: "Hardwood Flooring",
    parentPath: "/hardwood-floor",
    shortDescription:
      "Hardwood installation, refinishing, recoating, and repair — restoring real wood floors to their original beauty.",
    heroImage:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=2000",
    bullets: [
      "Solid and engineered hardwood installation",
      "Sand-and-refinish with dust containment",
      "Buff & recoat for tired finishes",
      "Board replacement and seamless repairs",
      "Eco-friendly water-based finishes available",
    ],
    cityAngle: (city) =>
      `Many ${city.name} homes feature original or vintage hardwood. We refinish and repair without unnecessary replacement, keeping the character your floors were built with.`,
  },
  {
    slug: "installation",
    name: "Flooring & Surface Installation",
    parentPath: "/installation",
    shortDescription:
      "Professional installation of hardwood, stone, tile, laminate, vinyl, and quartz surfaces by master craftsmen.",
    heroImage:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=2000",
    bullets: [
      "Hardwood and engineered wood",
      "Natural stone and porcelain tile",
      "Luxury vinyl plank and laminate",
      "Stone and quartz countertops",
      "Subfloor prep and moisture mitigation",
    ],
    cityAngle: (city) =>
      `${city.name} homes range from coastal cottages to modern estates — we match the right product and installation method to your specific home and lifestyle.`,
  },
  {
    slug: "natural-stone",
    name: "Natural Stone Services",
    parentPath: "/marble-natural-stone",
    shortDescription:
      "Natural stone restoration, deep cleaning, sealing, and repair for travertine, limestone, slate, and more.",
    heroImage:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=2000",
    bullets: [
      "Travertine, limestone, slate, and onyx care",
      "Honing, polishing, and refinishing",
      "Deep cleaning and color enhancement",
      "Penetrating and topical sealing",
      "Chip, crack, and grout repair",
    ],
    cityAngle: (city) =>
      `${city.name} properties often feature large stone entryways, patios, and pool decks. Our restoration brings them back to showroom condition without harsh, surface-stripping methods.`,
  },
  {
    slug: "marble-restoration",
    name: "Marble Restoration",
    parentPath: "/marble-restoration",
    shortDescription:
      "Marble polishing, refinishing, etch removal, stain removal, and repair for floors, counters, showers, and vanities.",
    heroImage:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=2000",
    bullets: [
      "Diamond honing and high-gloss polishing",
      "Etch and water-spot removal",
      "Stain extraction and color correction",
      "Crack, chip, and lippage repair",
      "Long-lasting impregnating sealers",
    ],
    cityAngle: (city) =>
      `From statement foyers to spa baths, marble is everywhere in ${city.name} — and we restore it on-site without ripping anything out.`,
  },
  {
    slug: "concrete-restoration",
    name: "Concrete Restoration",
    parentPath: "/concrete-restoration",
    shortDescription:
      "Concrete grinding, polishing, staining, cleaning, and sealing for driveways, patios, garages, and interior floors.",
    heroImage:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=2000",
    bullets: [
      "Diamond grinding and high-gloss polishing",
      "Acid and water-based staining",
      "Pressure washing and degreasing",
      "Penetrating sealers for stain protection",
      "Crack repair and surface leveling",
    ],
    cityAngle: (city) =>
      `${city.name} driveways, patios, and garage floors take a beating from sun, salt, and traffic — our restoration system protects and beautifies in one visit.`,
  },
];

export function findCityServiceMatch(combinedSlug: string): {
  service: CityService;
  city: City;
} | null {
  for (const service of CITY_SERVICES) {
    for (const city of CITIES) {
      if (combinedSlug === `${service.slug}-${city.slug}`) {
        return { service, city };
      }
    }
  }
  return null;
}
